/**
 * 回放游标：在轨迹当前进度点画队员位置图标（轨迹色），与在线 HTML Marker 分离。
 */
import mapboxgl from 'mapbox-gl';
import { toMapLngLat, getActiveBasemapIdForCoord } from './basemapCoord';
import { resolveTrackColor } from './trackColorHelpers';
import { createTeardropMarkerElement } from './memberMarkers';

function pointTimeMs(p) {
  if (!p) return null;
  const raw = p.timestamp || p.time || p.located_at || p.created_at || p.timeIso;
  if (raw == null) return null;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? null : t;
}

/** 主轨 index 对齐到其它轨迹：优先同时刻最近点，否则按比例 */
export function pickPlaybackPointForTrack(track, primaryTrack, primaryIdx) {
  if (!Array.isArray(track) || !track.length) return null;
  const idx = Math.min(Math.max(0, Number(primaryIdx) || 0), (primaryTrack?.length || 1) - 1);
  if (!primaryTrack?.length || track === primaryTrack) {
    return track[Math.min(idx, track.length - 1)];
  }
  const t = pointTimeMs(primaryTrack[idx]);
  if (t != null) {
    let best = track[0];
    let bestDiff = Infinity;
    for (let i = 0; i < track.length; i += 1) {
      const ti = pointTimeMs(track[i]);
      if (ti == null) continue;
      const d = Math.abs(ti - t);
      if (d < bestDiff) {
        bestDiff = d;
        best = track[i];
      }
    }
    return best;
  }
  const ratio = primaryTrack.length > 1 ? idx / (primaryTrack.length - 1) : 0;
  return track[Math.min(track.length - 1, Math.round(ratio * (track.length - 1)))];
}

function removeMarkerSafe(marker) {
  try { marker?.remove(); } catch (_) { /* ignore */ }
}

export function clearPlaybackCursorMarkers(markersRef) {
  if (!markersRef) return;
  const list = markersRef.current;
  if (Array.isArray(list)) {
    list.forEach(removeMarkerSafe);
  } else if (list && typeof list.forEach === 'function') {
    list.forEach(removeMarkerSafe);
  }
  markersRef.current = [];
}

/**
 * 按主轨进度在地图上同步各目标游标。
 * @returns {number} 实际画上的游标数
 */
export function syncPlaybackCursorMarkers(map, options = {}) {
  const {
    trackData = {},
    names = [],
    primaryName = null,
    index = 0,
    trackColors = {},
    members = [],
    markersRef
  } = options;
  if (!map || !markersRef) return 0;

  clearPlaybackCursorMarkers(markersRef);

  const nameList = (Array.isArray(names) && names.length)
    ? names.filter(Boolean)
    : Object.keys(trackData || {}).filter((k) => (trackData[k] || []).length > 0);
  if (!nameList.length) return 0;

  let primaryKey = primaryName && nameList.includes(primaryName) ? primaryName : null;
  if (!primaryKey) {
    let bestLen = -1;
    nameList.forEach((n) => {
      const len = (trackData[n] || []).length;
      if (len > bestLen) {
        bestLen = len;
        primaryKey = n;
      }
    });
  }
  const primaryTrack = trackData[primaryKey] || [];
  if (!primaryTrack.length) return 0;

  const basemapId = getActiveBasemapIdForCoord(map);
  let drawn = 0;

  nameList.forEach((name) => {
    const track = trackData[name] || [];
    const point = pickPlaybackPointForTrack(track, primaryTrack, index);
    const lng = Number(point?.last_longitude ?? point?.longitude ?? point?.lng);
    const lat = Number(point?.last_latitude ?? point?.latitude ?? point?.lat);
    const center = (Number.isFinite(lng) && Number.isFinite(lat)) ? [lng, lat] : null;
    if (!center) return;
    const display = toMapLngLat(center[0], center[1], basemapId, map) || center;
    const member = (members || []).find((m) => m?.name === name || m?.device_unique_id === name);
    const color = resolveTrackColor(name, trackColors, {
      name,
      deviceUniqueId: member?.device_unique_id
    });
    const el = createTeardropMarkerElement({
      pinColor: color,
      title: `${name}（回放）`,
      iconKey: 'chrome:water_drop',
      fromApp: false
    });
    try {
      el.style.zIndex = '3';
      el.style.filter = 'drop-shadow(0 1px 2px rgba(0,0,0,0.45))';
    } catch (_) { /* ignore */ }

    try {
      const marker = new mapboxgl.Marker({
        element: el,
        anchor: 'bottom',
        pitchAlignment: 'viewport',
        rotationAlignment: 'viewport'
      })
        .setLngLat(display)
        .addTo(map);
      marker.__zhxtPlaybackName = name;
      markersRef.current.push(marker);
      drawn += 1;
    } catch (error) {
      console.warn('回放游标绘制失败', name, error);
    }
  });

  return drawn;
}

